/**
 * Чистые функции отчётов и настройки списков (АН-05, АН-09, АН-10): сборка настройки вида, операции отбора
 * по типам полей, форматирование ячеек, скачивание выгрузки. Без обращений к DOM, кроме `downloadBlob`.
 */
import {
  type AggFn,
  FILTER_OPS,
  type FilterOp,
  type SourceField,
  type SourceInfo,
  type ViewConfig,
  type ViewFilter,
  type ViewRow,
} from '@epc/contracts';
import { ApiError } from '../../lib/api';
import { date, percent, tenge } from '../../lib/format';

/** Пустая настройка: колонки источника по умолчанию */
export function emptyConfig(): ViewConfig {
  return { columns: [], filters: [], groupBy: [], sort: [], totals: [], viewKind: 'table' };
}

/** Настройка, в которой видны колонки источника «по умолчанию» (для первого открытия списка) */
export function defaultConfigFor(src: SourceInfo): ViewConfig {
  return {
    ...emptyConfig(),
    columns: src.defaultColumns
      .filter((k) => src.fields.some((f) => f.key === k))
      .map((key) => ({ key, visible: true })),
  };
}

/** Колонки для редактора: сначала выбранные по порядку, затем остальные поля источника скрытыми */
export function editorColumns(src: SourceInfo, cfg: ViewConfig): Array<{ key: string; visible: boolean }> {
  const known = new Set(src.fields.map((f) => f.key));
  const chosen = cfg.columns.filter((c) => known.has(c.key)).map((c) => ({ key: c.key, visible: c.visible }));
  if (!chosen.length) {
    const def = src.defaultColumns.filter((k) => known.has(k));
    chosen.push(...def.map((key) => ({ key, visible: true })));
  }
  const seen = new Set(chosen.map((c) => c.key));
  for (const f of src.fields) if (!seen.has(f.key)) chosen.push({ key: f.key, visible: false });
  return chosen;
}

export function moveColumn<T>(list: T[], index: number, delta: -1 | 1): T[] {
  const to = index + delta;
  if (to < 0 || to >= list.length) return list;
  const next = list.slice();
  const [item] = next.splice(index, 1);
  next.splice(to, 0, item as T);
  return next;
}

/** Какие операции отбора разумны для поля такого типа */
export function opsFor(f: SourceField): FilterOp[] {
  switch (f.type) {
    case 'text':
      return ['contains', 'eq', 'ne', 'empty', 'notempty'];
    case 'number':
    case 'date':
      return ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between', 'empty', 'notempty'];
    case 'enum':
      return ['eq', 'ne', 'in', 'empty', 'notempty'];
    case 'bool':
      return ['eq'];
  }
}

export const opTitle = (op: FilterOp) => FILTER_OPS[op];

/** Новый отбор по полю: первая подходящая операция */
export function newFilter(f: SourceField): ViewFilter {
  const op = opsFor(f)[0] as FilterOp;
  return { field: f.key, op, value: f.type === 'bool' ? true : '' };
}

/** Отбор без значения не отправляется: пустое «равно» отсекло бы все строки */
export function isFilterComplete(f: ViewFilter): boolean {
  if (f.op === 'empty' || f.op === 'notempty') return true;
  if (f.op === 'in') return (f.values?.length ?? 0) > 0;
  if (f.op === 'between') return f.value != null && f.value !== '' && f.value2 != null && f.value2 !== '';
  return f.value !== null && f.value !== undefined && f.value !== '';
}

/** Настройка, готовая к отправке: без недописанных отборов и колонок, которых нет в источнике */
export function cleanConfig(cfg: ViewConfig, src: SourceInfo | undefined): ViewConfig {
  const known = new Set(src?.fields.map((f) => f.key) ?? []);
  const ok = (k: string) => !src || known.has(k);
  return {
    ...cfg,
    columns: cfg.columns.filter((c) => ok(c.key)),
    filters: cfg.filters.filter((f) => ok(f.field) && isFilterComplete(f)),
    groupBy: cfg.groupBy.filter(ok),
    sort: cfg.sort.filter((s) => ok(s.field)),
    totals: cfg.totals.filter((t) => ok(t.field)),
    period: cfg.period && ok(cfg.period.field) && (cfg.period.from || cfg.period.to) ? cfg.period : null,
    chart: cfg.chart && ok(cfg.chart.x) ? cfg.chart : null,
  };
}

export const configKey = (cfg: ViewConfig) => JSON.stringify(cfg);

export function sameConfig(a: ViewConfig, b: ViewConfig): boolean {
  return configKey(a) === configKey(b);
}

/** Функции итогов, подходящие полю */
export function aggFnsFor(f: SourceField): AggFn[] {
  if (f.type === 'number') return ['sum', 'avg', 'min', 'max', 'count'];
  if (f.type === 'date') return ['min', 'max', 'count'];
  return ['count'];
}

const numFmt = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });

/** Значение ячейки простыми словами: деньги, проценты, даты, «да / нет», подписи списков */
export function formatCell(f: SourceField, v: ViewRow[string] | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  if (f.type === 'bool') return v === true || v === 'true' ? 'да' : 'нет';
  if (f.type === 'date' && typeof v === 'string') return date(v);
  if (f.type === 'enum') return f.options?.find((o) => o.value === String(v))?.label ?? String(v);
  if (f.type === 'number' && typeof v === 'number') {
    if (f.unit === 'money') return tenge(v);
    if (f.unit === 'pct') return percent(v);
    if (f.unit === 'days') return `${numFmt.format(v)} дн`;
    return numFmt.format(v);
  }
  return String(v);
}

/** Итог по ключу `<функция>:<поле>` в том же виде, что и ячейка поля */
export function formatTotal(f: SourceField | undefined, fn: AggFn, v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  if (fn === 'count') return numFmt.format(v);
  if (!f) return numFmt.format(v);
  if (f.type === 'date') return date(String(v));
  return formatCell(f, v);
}

export const totalKey = (fn: AggFn, field: string) => `${fn}:${field}`;

/** Сколько условий отбора, группировок и т. п. включено — для подписи на кнопке */
export function configSummary(cfg: ViewConfig): string {
  const parts: string[] = [];
  if (cfg.filters.length) parts.push(`отборов: ${cfg.filters.length}`);
  if (cfg.groupBy.length) parts.push(`группировка: ${cfg.groupBy.length}`);
  if (cfg.sort.length) parts.push(`сортировка: ${cfg.sort.length}`);
  return parts.join(' · ');
}

/** Виды, которые источник может нарисовать: карты нет, пока нет координат; календарь — только при поле даты */
export function viewKindsFor(src: SourceInfo): Array<'table' | 'board' | 'calendar' | 'chart'> {
  const kinds: Array<'table' | 'board' | 'calendar' | 'chart'> = ['table'];
  if (src.fields.some((f) => f.type === 'enum')) kinds.push('board');
  if (src.dateField) kinds.push('calendar');
  if (src.fields.some((f) => f.groupable)) kinds.push('chart');
  return kinds;
}

/** Имя файла из заголовка content-disposition (filename*=UTF-8''…) */
export function fileNameFrom(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1]);
    } catch {
      return fallback;
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ?? fallback;
}

export function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Скачивание файла из API с заголовком защиты от подделки запросов; ошибки приходят по-русски из API */
export async function downloadFromApi(
  path: string,
  opts: { method?: 'GET' | 'POST'; body?: unknown; fallbackName: string },
): Promise<void> {
  const headers: Record<string, string> = { 'x-epc-csrf': '1' };
  let body: string | undefined;
  if (opts.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method: opts.method ?? (body ? 'POST' : 'GET'),
      headers,
      body,
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiError(0, 'network', 'Нет связи с порталом. Повторите через минуту.');
  }
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as {
      error?: { code?: string; message?: string };
    } | null;
    throw new ApiError(
      res.status,
      data?.error?.code ?? `http_${res.status}`,
      data?.error?.message ?? `Ошибка ${res.status}`,
    );
  }
  downloadBlob(await res.blob(), fileNameFrom(res.headers.get('content-disposition'), opts.fallbackName));
}

/** Дата начала недели (понедельник) для любой даты ГГГГ-ММ-ДД — для подписи «неделя с …» */
export function mondayOf(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  const shift = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - shift);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}
